import React, { useState, useRef, useCallback, useEffect } from "react";
import { FaTimes, FaCheck, FaSearchMinus, FaSearchPlus, FaUndo } from "react-icons/fa";

/**
 * FramePositionEditor
 *
 * Modal that lets the admin drag-and-drop the user's preview image inside
 * a fixed Post Frame overlay so they can set the final position that will
 * be shown in the user app.
 *
 * Props:
 *  - frameImageUrl  : URL of the Post Frame PNG (overlay, usually with transparency)
 *  - userImageUrl   : URL of the user slot's preview image (or null)
 *  - initialPosition: { x: number (0-100 %), y: number (0-100 %), scale: number (0.1-2) }
 *  - onSave(position): called with { x, y, scale } when admin clicks "Apply"
 *  - onClose()      : called on cancel/close
 */
const FramePositionEditor = ({
  frameImageUrl,
  userImageUrl,
  initialPosition = { x: 50, y: 50, scale: 1 },
  onSave,
  onClose,
}) => {
  const containerRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ mouseX: 0, mouseY: 0, posX: 0, posY: 0 });

  const [pos, setPos] = useState({
    x: initialPosition.x ?? 50,
    y: initialPosition.y ?? 50,
    scale: initialPosition.scale ?? 1,
  });

  // Background (user) image scale — starts at 100% of the 1:1 canvas
  const [bgScale, setBgScale] = useState(initialPosition.bgScale ?? 1);

  // ── Drag handlers ──────────────────────────────────────────────
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    isDraggingRef.current = true;
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      posX: pos.x,
      posY: pos.y,
    };
  }, [pos]);

  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    isDraggingRef.current = true;
    dragStartRef.current = {
      mouseX: touch.clientX,
      mouseY: touch.clientY,
      posX: pos.x,
      posY: pos.y,
    };
  }, [pos]);

  const handleMove = useCallback((clientX, clientY) => {
    if (!isDraggingRef.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const dx = clientX - dragStartRef.current.mouseX;
    const dy = clientY - dragStartRef.current.mouseY;
    const newX = Math.max(0, Math.min(100, dragStartRef.current.posX + (dx / rect.width) * 100));
    const newY = Math.max(0, Math.min(100, dragStartRef.current.posY + (dy / rect.height) * 100));
    setPos((p) => ({ ...p, x: newX, y: newY }));
  }, []);

  const handleMouseMove = useCallback((e) => handleMove(e.clientX, e.clientY), [handleMove]);
  const handleTouchMove = useCallback((e) => {
    e.preventDefault();
    handleMove(e.touches[0].clientX, e.touches[0].clientY);
  }, [handleMove]);

  const stopDrag = useCallback(() => { isDraggingRef.current = false; }, []);

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", stopDrag);
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", stopDrag);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", stopDrag);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", stopDrag);
    };
  }, [handleMouseMove, handleTouchMove, stopDrag]);

  const handleScaleChange = (delta) => {
    setPos((p) => ({ ...p, scale: Math.max(0.2, Math.min(3, +(p.scale + delta).toFixed(2))) }));
  };

  const handleReset = () => {
    setPos({ x: 50, y: 50, scale: 1 });
    setBgScale(1);
  };

  const handleApply = () => {
    if (typeof onSave === "function") onSave({ x: pos.x, y: pos.y, scale: pos.scale, bgScale });
  };

  // ── Derived styles ──────────────────────────────────────────────
  // The user image is absolutely positioned and scaled; `translate(-50%, -50%)`
  // centres it on the (x, y) point so (50,50) = dead centre.
  const userImgStyle = {
    position: "absolute",
    left: `${pos.x}%`,
    top: `${pos.y}%`,
    transform: `translate(-50%, -50%) scale(${pos.scale})`,
    transformOrigin: "center center",
    cursor: isDraggingRef.current ? "grabbing" : "grab",
    userSelect: "none",
    pointerEvents: "auto",
    zIndex: 2,
    width: "60%",
    height: "60%",
    objectFit: "cover",
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl mx-4 overflow-hidden flex flex-col max-h-[95vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 bg-purple-700 text-white">
          <h3 className="font-semibold text-base">Frame Position Editor</h3>
          <button type="button" onClick={onClose} className="hover:opacity-80 p-1">
            <FaTimes />
          </button>
        </div>

        <div className="p-4 overflow-auto flex-1 space-y-4">
          {/* Instructions */}
          <p className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
            🖱️ <strong>Drag</strong> the frame to position it over the user image.
            Use <strong>🖼️ Frame</strong> slider to resize the frame and <strong>📷 BG</strong> slider to resize the background image (1:1 ratio).
          </p>

          {/* Canvas */}
          <div
            ref={containerRef}
            className="relative w-full select-none overflow-hidden rounded-lg border-2 border-purple-300 bg-gray-200"
            style={{ aspectRatio: "1 / 1" }}
          >
            {/* User image layer (static background, centered, 1:1 scalable) */}
            {userImageUrl ? (
              <img
                src={userImageUrl}
                alt="User preview"
                className="absolute pointer-events-none"
                style={{
                  zIndex: 1,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  left: "50%",
                  top: "50%",
                  transform: `translate(-50%, -50%) scale(${bgScale})`,
                  transformOrigin: "center center",
                  aspectRatio: "1 / 1",
                }}
                draggable={false}
              />
            ) : (
              <div
                className="absolute bg-gray-300 flex items-center justify-center text-gray-500 text-sm pointer-events-none"
                style={{
                  zIndex: 1,
                  width: "100%",
                  height: "100%",
                  left: "50%",
                  top: "50%",
                  transform: `translate(-50%, -50%) scale(${bgScale})`,
                  transformOrigin: "center center",
                }}
              >
                User Image
              </div>
            )}

            {/* Frame overlay (draggable, front layer) */}
            <img
              src={frameImageUrl}
              alt="Post Frame"
              style={userImgStyle}
              draggable={false}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
            />
          </div>

          {/* Controls */}
          <div className="space-y-3">
            {/* Background (user image) scale row */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-blue-600 w-20 shrink-0">📷 BG:</span>
              <button
                type="button"
                onClick={() => setBgScale((s) => Math.max(0.2, +(s - 0.1).toFixed(2)))}
                className="w-8 h-8 rounded-full bg-blue-100 hover:bg-blue-200 flex items-center justify-center shrink-0 text-blue-700"
              >
                <FaSearchMinus />
              </button>
              <input
                type="range"
                min="0.2"
                max="3"
                step="0.05"
                value={bgScale}
                onChange={(e) => setBgScale(parseFloat(e.target.value))}
                className="flex-1 accent-blue-500 h-2 cursor-pointer"
              />
              <button
                type="button"
                onClick={() => setBgScale((s) => Math.min(3, +(s + 0.1).toFixed(2)))}
                className="w-8 h-8 rounded-full bg-blue-100 hover:bg-blue-200 flex items-center justify-center shrink-0 text-blue-700"
              >
                <FaSearchPlus />
              </button>
              <span className="w-12 text-center text-sm font-semibold bg-blue-100 text-blue-700 rounded px-1 py-0.5">
                {bgScale.toFixed(2)}×
              </span>
            </div>

            {/* Frame scale row */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-purple-600 w-20 shrink-0">🖼️ Frame:</span>
              <button
                type="button"
                onClick={() => handleScaleChange(-0.1)}
                className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center shrink-0"
              >
                <FaSearchMinus />
              </button>
              {/* Slider */}
              <input
                type="range"
                min="0.2"
                max="3"
                step="0.05"
                value={pos.scale}
                onChange={(e) => setPos((p) => ({ ...p, scale: parseFloat(e.target.value) }))}
                className="flex-1 accent-purple-600 h-2 cursor-pointer"
              />
              <button
                type="button"
                onClick={() => handleScaleChange(0.1)}
                className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center shrink-0"
              >
                <FaSearchPlus />
              </button>
              <span className="w-12 text-center text-sm font-semibold bg-purple-100 text-purple-700 rounded px-1 py-0.5">
                {pos.scale.toFixed(2)}×
              </span>
            </div>

            {/* Position + Reset row */}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="text-xs text-gray-400 font-mono">
                x: {Math.round(pos.x)}% &nbsp; y: {Math.round(pos.y)}%
              </span>
              <button
                type="button"
                onClick={handleReset}
                className="flex items-center gap-1 px-3 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-sm text-gray-600"
              >
                <FaUndo className="text-xs" /> Reset
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-3 bg-gray-50 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm hover:bg-purple-700 flex items-center gap-2"
          >
            <FaCheck /> Apply Position
          </button>
        </div>
      </div>
    </div>
  );
};

export default FramePositionEditor;
